import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { giftLidPositionLabel } from "../components/GiftBoxLidEditor";
import { ProductImageWithFallbacks } from "../components/ProductImageWithFallbacks";
import { getCartTotals, isValidEmail } from "../utils/checkoutOrder";
import { formatInrAmount } from "../utils/formatInrAmount";
import { useDeliveryHints } from "../hooks/useDeliveryHints";
import { sessionMatchesEmail } from "../utils/customerSession";
import TrustBadges from "../components/TrustBadges";
import { BtnContent } from "../components/BtnIcon";
import {
  IoAddOutline,
  IoArrowBackOutline,
  IoCartOutline,
  IoRemoveOutline,
  IoStorefrontOutline,
  IoTrashOutline,
  IoLocationOutline,
} from "react-icons/io5";

const initialCustomer = {
  name: "",
  email: "",
  phone: "",
  address: "",
  pincode: "",
};

function Cart({ cart, removeFromCart, updateQuantity }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(initialCustomer);
  const [fieldErrors, setFieldErrors] = useState({});

  const { hints, hasSuggestions, sessionEmail } = useDeliveryHints(
    cart.length,
    setCustomer,
  );

  const { subtotal } = getCartTotals(cart);

  useEffect(() => {
    const fromNav = location.state?.customer;
    if (fromNav && typeof fromNav === "object") {
      setCustomer((c) => ({
        name: fromNav.name ?? c.name,
        email: fromNav.email ?? c.email,
        phone: fromNav.phone ?? c.phone,
        address: fromNav.address ?? c.address,
        pincode: fromNav.pincode ?? c.pincode,
      }));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const validateCustomer = () => {
    const err = {};
    if (!customer.name.trim()) err.name = "Name is required";
    if (!customer.email.trim()) err.email = "Email is required";
    else if (!isValidEmail(customer.email)) err.email = "Enter a valid email address";
    if (!customer.phone.trim()) err.phone = "Phone is required";
    else if ((customer.phone.match(/\d/g) || []).length < 10)
      err.phone = "Enter a valid phone number (at least 10 digits)";
    if (!customer.address.trim()) err.address = "Address is required";
    if (!customer.pincode.trim()) err.pincode = "Pincode is required";
    else if (!/^\d{6}$/.test(customer.pincode.trim()))
      err.pincode = "Use a 6-digit pincode";
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id);
      return;
    }
    updateQuantity(id, newQuantity);
  };

  const goToCheckout = () => {
    if (!validateCustomer()) return;
    const cust = {
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      pincode: customer.pincode,
    };
    if (sessionMatchesEmail(customer.email)) {
      navigate("/checkout", { state: { customer: cust } });
      return;
    }
    navigate("/checkout-verify", { state: { customer: cust } });
  };

  const onCustomerChange = (field, value) => {
    setCustomer((c) => ({ ...c, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((e) => ({ ...e, [field]: undefined }));
    }
  };

  return (
    <div className="container py-4">
      <h1 className="text-center mb-4">Shopping Cart</h1>

      {cart.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: "60px" }}>🛒</div>

          <h3>Your cart is empty</h3>

          <p>Start shopping to add beautiful gift boxes</p>

          <Link to="/shop" className="btn btn-primary mt-3">
            <BtnContent icon={IoStorefrontOutline}>Browse Gift Collection</BtnContent>
          </Link>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-lg-8 col-md-12 order-2 order-lg-1">
            {cart.map((item) => (
              <div className="card mb-3 shadow-sm" key={item.id}>
                <div className="row g-0 align-items-stretch">
                  <div className="col-md-3 col-4 p-0 d-flex">
                    <ProductImageWithFallbacks
                      urls={item.images}
                      alt={item.name}
                      className="w-100 h-100 rounded-start"
                      style={{ objectFit: "cover" }}
                      placeholder="https://via.placeholder.com/400x500"
                    />
                  </div>

                  <div className="col-md-6 col-8">
                    <div className="card-body py-2 px-3 d-flex flex-column justify-content-center">
                      <h6 className="card-title mb-1">{item.name}</h6>

                      <p className="text-muted small mb-1">
                        {item.boxSizeLabel || item.fabric} • {item.color}
                      </p>

                      <p className="price-figure mb-2">
                        ₹{formatInrAmount(item.price)} each
                      </p>

                      {item.giftLidMessage ? (
                        <p className="small fst-italic text-muted mb-2 border-start border-3 ps-2" style={{ borderColor: "var(--accent-primary)" }}>
                          <span className="fw-semibold d-block text-dark" style={{ fontSize: "0.7rem" }}>
                            Gift box message
                          </span>
                          {item.giftLidMessage}
                          {item.giftLidLayout ? (
                            <span className="d-block mt-1 small" style={{ fontSize: "0.68rem" }}>
                              Placement: {giftLidPositionLabel(item.giftLidLayout)}
                              {" · size "}
                              {Number(item.giftLidLayout.scale).toFixed(2)}×
                              {item.giftLidTextColor ? ` · ${item.giftLidTextColor}` : ""}
                            </span>
                          ) : item.giftLidTextColor ? (
                            <span className="d-block mt-1 small" style={{ fontSize: "0.68rem" }}>
                              Text colour: {item.giftLidTextColor}
                            </span>
                          ) : null}
                        </p>
                      ) : null}

                      <p className="mb-2">
                        <Link
                          to={`/customise/${item.id}`}
                          state={{
                            product: item,
                            editCartLine: true,
                            giftLidMessage: item.giftLidMessage || "",
                            giftLidLayout: item.giftLidLayout,
                            giftLidTextColor: item.giftLidTextColor,
                            returnTo: "/cart",
                          }}
                          className="small text-decoration-none"
                          style={{ color: "var(--accent-primary)" }}
                        >
                          {item.giftLidMessage ? "Edit gift box message" : "Add message on gift box"}
                        </Link>
                      </p>

                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                          className="btn btn-light btn-sm px-2"
                          onClick={() =>
                            handleQuantityChange(item.id, item.quantity - 1)
                          }
                          aria-label="Decrease quantity"
                        >
                          <IoRemoveOutline size={16} aria-hidden />
                        </button>

                        <span>{item.quantity}</span>

                        <button
                          className="btn btn-light btn-sm px-2"
                          onClick={() =>
                            handleQuantityChange(item.id, item.quantity + 1)
                          }
                          aria-label="Increase quantity"
                        >
                          <IoAddOutline size={16} aria-hidden />
                        </button>

                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <BtnContent icon={IoTrashOutline}>Remove</BtnContent>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3 text-md-end text-start d-flex align-items-center justify-content-md-end px-3 py-2 price-figure">
                    ₹{formatInrAmount(item.price * item.quantity)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="col-lg-4 col-md-12 d-flex flex-column gap-3 order-1 order-lg-2">
            <div className="card shadow-sm p-4 order-2 order-lg-1">
              <h4 className="mb-3">Delivery details</h4>
              {sessionEmail && (
                <p className="small text-muted mb-3">
                  Signed in as <strong>{sessionEmail}</strong>
                  {hasSuggestions
                    ? " — use suggestions below or type something different."
                    : " — we filled your details from past orders when possible."}
                </p>
              )}

              <datalist id="cust-dl-name">
                {hints.names.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
              <datalist id="cust-dl-phone">
                {hints.phones.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <datalist id="cust-dl-pin">
                {hints.pincodes.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>

              <div className="mb-3">
                <label className="form-label small fw-semibold" htmlFor="cust-name">
                  Full name
                </label>
                <input
                  id="cust-name"
                  type="text"
                  className={`form-control form-control-sm ${fieldErrors.name ? "is-invalid" : ""}`}
                  autoComplete="name"
                  list={hints.names.length > 0 ? "cust-dl-name" : undefined}
                  value={customer.name}
                  onChange={(e) => onCustomerChange("name", e.target.value)}
                  placeholder="Your name"
                />
                {fieldErrors.name && (
                  <div className="invalid-feedback d-block">{fieldErrors.name}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold" htmlFor="cust-email">
                  Email
                </label>
                <input
                  id="cust-email"
                  type="email"
                  className={`form-control form-control-sm ${fieldErrors.email ? "is-invalid" : ""}`}
                  autoComplete="email"
                  value={customer.email}
                  onChange={(e) => onCustomerChange("email", e.target.value)}
                  placeholder="you@example.com"
                />
                {fieldErrors.email && (
                  <div className="invalid-feedback d-block">{fieldErrors.email}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold" htmlFor="cust-phone">
                  Phone
                </label>
                <input
                  id="cust-phone"
                  type="tel"
                  className={`form-control form-control-sm ${fieldErrors.phone ? "is-invalid" : ""}`}
                  autoComplete="tel"
                  list={hints.phones.length > 0 ? "cust-dl-phone" : undefined}
                  value={customer.phone}
                  onChange={(e) => onCustomerChange("phone", e.target.value)}
                  placeholder="10-digit mobile"
                />
                {fieldErrors.phone && (
                  <div className="invalid-feedback d-block">{fieldErrors.phone}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold" htmlFor="cust-address">
                  Address
                </label>
                <textarea
                  id="cust-address"
                  className={`form-control form-control-sm ${fieldErrors.address ? "is-invalid" : ""}`}
                  autoComplete="street-address"
                  rows={3}
                  value={customer.address}
                  onChange={(e) => onCustomerChange("address", e.target.value)}
                  placeholder="House / street, area, city, state"
                />
                {hints.addresses.length > 1 && (
                  <div className="mt-2">
                    <span className="small text-muted d-block mb-1">Previous addresses (tap to use):</span>
                    <div className="d-flex flex-wrap gap-1">
                      {hints.addresses.map((a) => (
                        <button
                          key={a}
                          type="button"
                          className="btn btn-outline-secondary btn-sm text-start"
                          style={{ maxWidth: "100%", whiteSpace: "normal", fontSize: "0.75rem" }}
                          onClick={() => onCustomerChange("address", a)}
                        >
                          <BtnContent icon={IoLocationOutline} iconSize={14}>
                            {a.length > 80 ? `${a.slice(0, 80)}…` : a}
                          </BtnContent>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {fieldErrors.address && (
                  <div className="invalid-feedback d-block">{fieldErrors.address}</div>
                )}
              </div>

              <div className="mb-0">
                <label className="form-label small fw-semibold" htmlFor="cust-pin">
                  Pincode
                </label>
                <input
                  id="cust-pin"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className={`form-control form-control-sm ${fieldErrors.pincode ? "is-invalid" : ""}`}
                  autoComplete="postal-code"
                  list={hints.pincodes.length > 0 ? "cust-dl-pin" : undefined}
                  value={customer.pincode}
                  onChange={(e) =>
                    onCustomerChange("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="6 digits"
                />
                {fieldErrors.pincode && (
                  <div className="invalid-feedback d-block">{fieldErrors.pincode}</div>
                )}
              </div>
            </div>

            <div className="card shadow-sm p-4 order-1 order-lg-2">
              <h4 className="mb-3">Order Summary</h4>

              <div className="d-flex justify-content-between align-items-baseline mb-4">
                <span className="fw-semibold">
                  Total ({cart.reduce((sum, i) => sum + i.quantity, 0)} items)
                </span>
                <span className="price-figure price-figure--emphasis">
                  ₹{formatInrAmount(subtotal)}
                </span>
              </div>

              <p className="small text-muted mb-3">
                {sessionMatchesEmail(customer.email)
                  ? "You are verified for this email — checkout opens directly."
                  : "Next step: we email you a one-time code to confirm this address before you pay."}
              </p>

              <TrustBadges className="mb-3" />

              <div className="checkout-action-buttons d-grid gap-2 mb-2">
                <button
                  type="button"
                  onClick={goToCheckout}
                  className="btn btn-primary py-2 fw-semibold checkout-action-primary"
                >
                  <BtnContent icon={IoCartOutline}>Checkout</BtnContent>
                </button>
                <Link
                  to="/shop"
                  className="btn btn-outline-secondary py-2 checkout-back-to-cart"
                >
                  <BtnContent icon={IoArrowBackOutline}>Continue shopping</BtnContent>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;
